import { useCallback } from 'react';
import { StringParam, useQueryParam } from 'use-query-params';

export type UseSelectedSuggestion = {
  selectedId: string | undefined;
  select: (id: string) => void;
  /** The draft is gone — approved or discarded — so nothing should stay chosen. */
  clearSelection: () => void;
};

/**
 * Which suggestion the reviewer is looking at, kept in the URL.
 *
 * A link to one draft is then a link anyone can send or reload, and something
 * outside the queue can hand a reviewer a draft to look at by navigating here
 * with the id. The preview reads the draft by id, so one the list has not
 * loaded resolves on its own.
 *
 * `replaceIn` rather than a new history entry per row: working through a queue
 * is one visit, and Back should leave the page rather than walk back through
 * every draft that was opened on the way.
 */
export function useSelectedSuggestion(): UseSelectedSuggestion {
  const [selectedId, setSelectedId] = useQueryParam(
    'suggestionId',
    StringParam,
  );

  const select = useCallback(
    (id: string) => setSelectedId(id, 'replaceIn'),
    [setSelectedId],
  );

  const clearSelection = useCallback(
    // `undefined` drops the parameter rather than leaving `?suggestionId=`.
    () => setSelectedId(undefined, 'replaceIn'),
    [setSelectedId],
  );

  return { selectedId: selectedId ?? undefined, select, clearSelection };
}
